import { getFloristStatusConfig } from '@/components/organizations/FloristStatusBadge'
import { Typography } from '@/components/ui/typography'
import { useGoogleMaps } from '@/hooks/use-google-maps'
import { getGoogleMapsMapId } from '@/lib/google-maps-api-key'
import type { GoogleMapsLibraryName } from '@/lib/google-maps-loader'
import type { FloristMapLocation } from '@/lib/services/reports/reports.types'
import { cn } from '@/lib/utils'
import { MarkerClusterer } from '@googlemaps/markerclusterer'
import { useEffect, useEffectEvent, useRef, useState, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'

/** A florist location with a map pin. */
export type PinnedFloristLocation = FloristMapLocation & { lat: number; lng: number }

export function hasPin(location: FloristMapLocation): location is PinnedFloristLocation {
  return location.lat !== null && location.lng !== null
}

const MAP_LIBRARIES: GoogleMapsLibraryName[] = ['maps', 'marker']

const CONTIGUOUS_US: google.maps.LatLngBoundsLiteral = {
  north: 49.4,
  south: 24.4,
  west: -125,
  east: -66.9,
}

/** Frame the contiguous US: the view the map opens on. */
export function showWholeCountry(map: google.maps.Map): void {
  map.fitBounds(CONTIGUOUS_US, 0)
}

/** Frame these shops: all of them in view, but no closer than one shop's streets. */
function showLocations(map: google.maps.Map, locations: readonly PinnedFloristLocation[]): void {
  const bounds = new window.google.maps.LatLngBounds()
  locations.forEach((location) => bounds.extend({ lat: location.lat, lng: location.lng }))
  const span = bounds.toSpan()

  if (span.lat() < 0.01 && span.lng() < 0.01) {
    map.setCenter(bounds.getCenter())
    map.setZoom(14)
  } else {
    map.fitBounds(bounds, 48)
  }
}

function pinElement(location: PinnedFloristLocation): HTMLElement {
  const pin = document.createElement('div')
  pin.className = cn(
    'size-3.5 rounded-full border-2 border-background shadow-md',
    getFloristStatusConfig(location.florist_status).pinClassName
  )

  return pin
}

function clusterElement(count: number): HTMLElement {
  const bubble = document.createElement('div')
  bubble.className = cn(
    'flex items-center justify-center rounded-full border-2 border-background bg-primary text-xs font-semibold text-primary-foreground shadow-md',
    count < 10 ? 'size-8' : count < 100 ? 'size-10' : 'size-12'
  )
  bubble.textContent = String(count)

  return bubble
}

type FloristMapProps = {
  /** Keep this array stable between renders: a new one redraws the pins. */
  locations: readonly PinnedFloristLocation[]
  /** The shops to move the map to when they change; null leaves the map where it is. */
  frame: readonly PinnedFloristLocation[] | null
  /** Holds the map once it exists, so the page can move it from its own event handlers. */
  mapRef: RefObject<google.maps.Map | null>
  /** The location the pop-up opens over; none when nothing on the map is selected. */
  selectedId: number | null
  /** What the pop-up shows. */
  popup: ReactNode
  onBoundsChange: (bounds: google.maps.LatLngBounds) => void
  onSelect: (location: PinnedFloristLocation) => void
  onDeselect: () => void
  className?: string
}

/**
 * Every pinned florist location, grouped into numbered bubbles that split apart as you zoom in.
 * A pop-up opens over the selected shop, as on Google Maps.
 */
export function FloristMap({
  locations,
  frame,
  mapRef,
  selectedId,
  popup,
  onBoundsChange,
  onSelect,
  onDeselect,
  className,
}: FloristMapProps) {
  const { maps, marker, error } = useGoogleMaps(MAP_LIBRARIES)
  const containerRef = useRef<HTMLDivElement>(null)
  const clustererRef = useRef<MarkerClusterer | null>(null)
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null)
  // Each shop's pin, made once: searching changes the shown shops on every keystroke, and the
  // clusterer leaves pins it already shows in place instead of rebuilding them all.
  const pinsRef = useRef(
    new WeakMap<PinnedFloristLocation, google.maps.marker.AdvancedMarkerElement>()
  )
  // The pop-up's content lives in this node; React renders into it through a portal.
  const [popupNode] = useState(() => document.createElement('div'))
  const reportBounds = useEffectEvent((map: google.maps.Map) => {
    const bounds = map.getBounds()
    if (bounds) onBoundsChange(bounds)
  })
  const selectLocation = useEffectEvent((location: PinnedFloristLocation) => onSelect(location))
  const deselect = useEffectEvent(() => onDeselect())
  // The view the map opens on: the shops it's asked to show (a link can open on a search), else
  // the whole country. Moving it again right after it's made doesn't stick, as Google applies the
  // opening view once the map has its size.
  const showOpeningView = useEffectEvent((map: google.maps.Map) => {
    if (frame && frame.length > 0) {
      showLocations(map, frame)
    } else {
      showWholeCountry(map)
    }
  })

  // The map, its clusterer and its pop-up belong to Google's Maps API; create them once it loads.
  useEffect(() => {
    if (!maps || !marker || !containerRef.current) return

    const map = new maps.Map(containerRef.current, {
      center: { lat: 39.8, lng: -98.6 },
      zoom: 4,
      mapId: getGoogleMapsMapId(),
      mapTypeControl: false,
      streetViewControl: false,
      clickableIcons: false,
    })
    showOpeningView(map)
    // No header row or close button: clicking the map or pressing Escape closes it. The offset
    // lifts its point from the pin's base to its top.
    const infoWindow = new maps.InfoWindow({
      content: popupNode,
      headerDisabled: true,
      pixelOffset: new window.google.maps.Size(0, -14),
    })
    const clusterer = new MarkerClusterer({
      map,
      renderer: {
        render: ({ count, position }) =>
          new marker.AdvancedMarkerElement({
            position,
            content: clusterElement(count),
            title: `${count} locations`,
            zIndex: 1000 + count,
          }),
      },
    })
    const listeners = [
      map.addListener('idle', () => reportBounds(map)),
      // Clicking the map itself closes the pop-up, as on Google Maps.
      map.addListener('click', () => deselect()),
    ]
    mapRef.current = map
    clustererRef.current = clusterer
    infoWindowRef.current = infoWindow

    return () => {
      listeners.forEach((listener) => listener.remove())
      infoWindow.close()
      clusterer.setMap(null)
      clustererRef.current = null
      infoWindowRef.current = null
      mapRef.current = null
    }
  }, [maps, marker, mapRef, popupNode])

  // Swap the pins whenever the page shows a different set of locations.
  useEffect(() => {
    const clusterer = clustererRef.current
    if (!marker || !clusterer) return

    const pins = locations.map((location) => {
      const saved = pinsRef.current.get(location)
      if (saved) return saved

      const pin = new marker.AdvancedMarkerElement({
        position: { lat: location.lat, lng: location.lng },
        content: pinElement(location),
        title: location.organization_name,
        gmpClickable: true,
      })
      pin.addEventListener('gmp-click', () => selectLocation(location))
      pinsRef.current.set(location, pin)

      return pin
    })
    clusterer.addMarkers(pins)

    // Redrawn once, with the next set of pins.
    return () => clusterer.clearMarkers(true)
  }, [marker, locations])

  // Move the map when it's asked to show different shops, not when the same shops arrive again
  // (a refetch), which would undo where the person moved it since. A map made later opens on them.
  // Sorted: the same shops can come back from a refetch in another order.
  const frameKey =
    frame
      ?.map((location) => location.id)
      .toSorted((a, b) => a - b)
      .join() ?? ''
  const showFrame = useEffectEvent(() => {
    const map = mapRef.current
    if (map && frame && frame.length > 0) showLocations(map, frame)
  })
  useEffect(() => showFrame(), [frameKey])

  // Open the pop-up over the selected shop, or close it. It stands on the shop's spot rather than
  // its pin, so it stays put while zooming tucks the pin into a numbered bubble (as with shops
  // that share an address), the way Google Maps keeps a picked place open.
  useEffect(() => {
    const infoWindow = infoWindowRef.current
    const location = locations.find((candidate) => candidate.id === selectedId)
    if (!infoWindow) return
    if (!location) {
      infoWindow.close()
      return
    }

    infoWindow.setPosition({ lat: location.lat, lng: location.lng })
    infoWindow.open({ map: mapRef.current, shouldFocus: false })
    // `maps` runs this again once the map exists, for a shop picked from the list before then.
  }, [selectedId, locations, mapRef, maps])

  // Escape closes the open pop-up, unless something else (a menu, a suggestion list) took it.
  useEffect(() => {
    if (selectedId === null) return

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) deselect()
    }
    document.addEventListener('keydown', closeOnEscape)

    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [selectedId])

  return (
    <div className={cn('florist-map relative', className)}>
      <div ref={containerRef} className="size-full" />
      {createPortal(popup, popupNode)}
      {error ? (
        <div className="absolute inset-0 flex items-center justify-center p-6">
          <Typography variant="body" tone="muted" align="center">
            The map couldn't load. Refresh the page to try again.
          </Typography>
        </div>
      ) : null}
    </div>
  )
}
